/*
 * terrain.js — map layers beyond the basemap.
 *
 *   GpsTerrain.hillshade(L, { urls, pane, onFail }) → L.GridLayer
 *       Relief shading computed IN THE BROWSER from Terrarium elevation tiles
 *       (the same tiles dem.js samples; on ru they come through our proxy).
 *       No third-party shaded-relief service: the picture follows the data
 *       we already trust for elevation, and there is nothing else to block.
 *   GpsTerrain.shade(urls, z, x, y, palette) → Promise<canvas|null>
 *       One shaded tile (z ≤ 15) — the video renderer draws these under its
 *       own camera. Tiles are fetched once per page and shared by both users.
 *   GpsTerrain.chain(L, map, urls, opts) → L.TileLayer that walks a list of
 *       hosts in order when one stops answering (used for satellite imagery).
 *
 * Hillshade: Horn's 3×3 gradient, sun from the north-west at 45°, the classic
 * cartographic light. The relief is exaggerated more the further out you zoom
 * (a 150 m pixel averages slopes away): z-factor 1.3 at z14 → 10 at z7.
 * Only the deviation from «flat» is painted — shadows dark, sunlit slopes
 * light — so the layer reads on the dark and the light basemap alike and a
 * flat city stays untouched. Sea (≤ 0 m) is left clear.
 */
(function () {
    'use strict';
    var MAXZ = 15, SIZE = 256, RAD = Math.PI / 180;
    var ZEN = 45 * RAD, AZ = (360 - 315 + 90) * RAD; // sun altitude 45°, azimuth 315°
    var COSZ = Math.cos(ZEN), SINZ = Math.sin(ZEN);
    // the map's palette: soft, relief under a map rather than a stencil over it
    var MAP = { sh: [22, 28, 46, 92], li: [255, 252, 240, 46] };

    /* Heights live in the pixel values, so the tile is decoded with colour
     * management OFF (as dem.js does): a converted tile is a changed terrain. */
    function loadImg(url) {
        if (typeof createImageBitmap === 'function' && typeof fetch === 'function') {
            // a host that does not answer must not hold the tile forever: 8 s, then the next host
            var ac = typeof AbortController !== 'undefined' ? new AbortController() : null;
            var tm = ac ? setTimeout(function () { ac.abort(); }, 8000) : 0;
            return fetch(url, { mode: 'cors', credentials: 'omit', signal: ac ? ac.signal : undefined }).then(function (r) {
                if (!r.ok) return null;
                return r.blob().then(function (b) { return createImageBitmap(b, { colorSpaceConversion: 'none', premultiplyAlpha: 'none' }); });
            }).catch(function () { return null; }).then(function (v) { clearTimeout(tm); return v; });
        }
        return new Promise(function (res) {
            var im = new Image();
            im.crossOrigin = 'anonymous';
            im.onload = function () { res(im); };
            im.onerror = function () { res(null); };
            im.src = url;
        });
    }

    function tileLat(y, z) { // latitude of a tile's centre row
        var n = Math.PI - 2 * Math.PI * (y + 0.5) / Math.pow(2, z);
        return Math.atan(0.5 * (Math.exp(n) - Math.exp(-n))) / RAD;
    }

    /* Terrarium image → heights on a grid padded by one cell on every side.
     * The pad is EXTRAPOLATED (2·edge − next), not copied: a copied edge reads
     * as flat ground, and every tile got a bright or dark seam around it. */
    function heights(img) {
        var c = document.createElement('canvas');
        c.width = SIZE; c.height = SIZE;
        var x = c.getContext('2d', { willReadFrequently: true });
        x.drawImage(img, 0, 0);
        if (img.close) img.close();
        var src;
        try { src = x.getImageData(0, 0, SIZE, SIZE); } catch (e) { return null; } // tainted: no CORS
        var d = src.data, P = SIZE + 2, H = new Float32Array(P * P), i;
        for (var r = 0; r < SIZE; r++) for (var q = 0; q < SIZE; q++) {
            var p = (r * SIZE + q) * 4;
            H[(r + 1) * P + q + 1] = d[p] * 256 + d[p + 1] + d[p + 2] / 256 - 32768;
        }
        for (i = 1; i <= SIZE; i++) {
            H[i] = 2 * H[P + i] - H[2 * P + i];                                   // top
            H[(P - 1) * P + i] = 2 * H[(P - 2) * P + i] - H[(P - 3) * P + i];     // bottom
            H[i * P] = 2 * H[i * P + 1] - H[i * P + 2];                           // left
            H[i * P + P - 1] = 2 * H[i * P + P - 2] - H[i * P + P - 3];           // right
        }
        H[0] = H[P + 1]; H[P - 1] = H[P + P - 2]; H[(P - 1) * P] = H[(P - 2) * P + 1]; H[P * P - 1] = H[(P - 1) * P - 2];
        return H;
    }

    /* The shade of one tile as an RGBA canvas (256²). */
    function shadeTile(H, z, y, pal) {
        pal = pal || MAP;
        var P = SIZE + 2;
        var c = document.createElement('canvas');
        c.width = SIZE; c.height = SIZE;
        var x = c.getContext('2d');
        var cell = 40075016.686 * Math.cos(tileLat(y, z) * RAD) / (SIZE * Math.pow(2, z));
        var zf = Math.max(1.3, Math.min(10, Math.pow(2, (14 - z) * 0.5)));
        var k = zf / (8 * cell);
        var out = x.createImageData(SIZE, SIZE), o = out.data;
        var S = pal.sh, Li = pal.li;
        for (var rr = 0; rr < SIZE; rr++) {
            for (var qq = 0; qq < SIZE; qq++) {
                var c0 = (rr + 1) * P + qq + 1;
                if (H[c0] <= 0.5) continue; // sea and no-data stay clear
                var a = H[c0 - P - 1], b = H[c0 - P], cc = H[c0 - P + 1];
                var dd = H[c0 - 1], f = H[c0 + 1];
                var g = H[c0 + P - 1], h = H[c0 + P], ii = H[c0 + P + 1];
                var dzdx = ((cc + 2 * f + ii) - (a + 2 * dd + g)) * k;
                var dzdy = ((g + 2 * h + ii) - (a + 2 * b + cc)) * k;
                var slope = Math.atan(Math.sqrt(dzdx * dzdx + dzdy * dzdy));
                var aspect = Math.atan2(dzdy, -dzdx);
                var s = COSZ * Math.cos(slope) + SINZ * Math.sin(slope) * Math.cos(AZ - aspect);
                var p2 = (rr * SIZE + qq) * 4;
                if (s < COSZ) { // in shadow
                    o[p2] = S[0]; o[p2 + 1] = S[1]; o[p2 + 2] = S[2];
                    o[p2 + 3] = Math.round(S[3] * Math.pow((COSZ - s) / COSZ, 0.85));
                } else { // lit slope
                    o[p2] = Li[0]; o[p2 + 1] = Li[1]; o[p2 + 2] = Li[2];
                    o[p2 + 3] = Math.round(Li[3] * Math.pow((s - COSZ) / (1 - COSZ), 0.85));
                }
            }
        }
        x.putImageData(out, 0, 0);
        return c;
    }

    // ── one fetcher for the page: heights per tile, shades per palette ──────
    var hCache = new Map(), hOrder = [], sCache = new Map(), sOrder = [];
    var hostOf = {}; // chain → index of the host that last answered
    function fetchHeights(urls, z, x, y) {
        var key = urls[0] + '|' + z + '/' + x + '/' + y;
        if (hCache.has(key)) return hCache.get(key);
        var ck = urls[0], start = hostOf[ck] || 0, tried = 0;
        var p = (function next() {
            if (tried >= urls.length) return Promise.resolve(null);
            var hi = (start + tried) % urls.length;
            var u = urls[hi].replace('{z}', z).replace('{x}', x).replace('{y}', y);
            tried++;
            return loadImg(u).then(function (im) {
                if (im) { hostOf[ck] = hi; return heights(im); }
                return next();
            });
        })();
        hCache.set(key, p); hOrder.push(key);
        if (hOrder.length > 160) hCache.delete(hOrder.shift());
        return p;
    }
    function shade(urls, z, x, y, pal) {
        pal = pal || MAP;
        var key = pal.sh.join(',') + pal.li.join(',') + '|' + z + '/' + x + '/' + y;
        if (sCache.has(key)) return sCache.get(key);
        var p = fetchHeights(urls, z, x, y).then(function (H) { return H ? shadeTile(H, z, y, pal) : null; });
        sCache.set(key, p); sOrder.push(key);
        if (sOrder.length > 200) sCache.delete(sOrder.shift());
        return p;
    }

    function hillshade(L, opts) {
        opts = opts || {};
        var urls = opts.urls || [];
        var Layer = L.GridLayer.extend({
            createTile: function (coords, done) {
                var tile = document.createElement('canvas');
                var ts = this.getTileSize();
                tile.width = ts.x; tile.height = ts.y;
                var z = coords.z, xx = coords.x, yy = coords.y, sx = 0, sy = 0, span = SIZE;
                if (z > MAXZ) { // over-zoom: a part of the z15 tile, enlarged
                    var f = Math.pow(2, z - MAXZ);
                    sx = (xx % f) * SIZE / f; sy = (yy % f) * SIZE / f; span = SIZE / f;
                    xx = Math.floor(xx / f); yy = Math.floor(yy / f); z = MAXZ;
                }
                var n = Math.pow(2, z);
                if (yy < 0 || yy >= n) { setTimeout(function () { done(null, tile); }, 0); return tile; }
                xx = ((xx % n) + n) % n;
                shade(urls, z, xx, yy).then(function (sh) {
                    if (sh) {
                        var cx = tile.getContext('2d');
                        cx.imageSmoothingEnabled = true;
                        cx.drawImage(sh, sx, sy, span, span, 0, 0, ts.x, ts.y);
                    } else if (opts.onFail) opts.onFail();
                    done(null, tile);
                }, function (e) { done(e, tile); });
                return tile;
            }
        });
        return new Layer({ pane: opts.pane || 'overlayPane', opacity: opts.opacity != null ? opts.opacity : 1, maxZoom: 19, maxNativeZoom: 19, updateWhenIdle: true, keepBuffer: 1, className: 'gt-relief' });
    }

    /* A raster layer that walks its host list: 8 failed tiles in a row and the
     * next host takes over (the same threshold map-tiles.js uses). */
    function chain(L, map, urls, opts) {
        opts = opts || {};
        var idx = 0, fails = 0;
        var o = { maxZoom: 19, maxNativeZoom: opts.maxNativeZoom || 19, attribution: opts.attribution || '', pane: opts.pane || 'tilePane' };
        var l = L.tileLayer(urls[0], o);
        l.on('tileerror', function () {
            fails++;
            if (fails < 8) return;
            fails = 0;
            if (idx + 1 < urls.length) { idx++; l.setUrl(urls[idx]); if (opts.onSwitch) opts.onSwitch(idx); }
            else if (opts.onExhausted) opts.onExhausted();
        });
        l.on('tileload', function () { fails = 0; });
        return l;
    }

    window.GpsTerrain = { hillshade: hillshade, shade: shade, chain: chain, MAXZ: MAXZ, _shadeTile: shadeTile, _heights: heights };
})();
